/**
 * earcons.js — Tonos cortos que anteceden a la locución de una alerta.
 *
 * Por qué un tono y no solo la voz: la voz tarda ~2 s en decir la frase, y el
 * primer medio segundo se pierde mientras el oído "engancha". Un earcon de
 * 200 ms delante hace de campanilla — para cuando arranca la frase ya se está
 * escuchando, y la familia de la alerta se reconoce antes de la primera
 * palabra.
 *
 * Por qué sintetizados y no archivos .mp3: un tono es una envolvente y dos
 * frecuencias. Como assets serían 6 descargas más en el precache del SW, 6
 * archivos que versionar y un salto de tamaño del bundle, para reproducir algo
 * que el navegador genera exacto en 30 líneas. `alert-critical.mp3` se queda
 * donde está — lo usa el click de notificación, que es otro camino.
 *
 * Las familias son las MISMAS que los patrones de vibración de `sw.js`: quien
 * siente el patrón en el bolsillo y luego escucha el tono en el escritorio
 * aprende un solo vocabulario, no dos.
 *
 * ## En la app el tono NO lo toca este módulo
 *
 * Un AudioContext adentro del WebView sale por el stream de MÚSICA, que es
 * justo el que queda en cero cuando alguien silencia el teléfono de noche: el
 * tono se perdía mientras la voz —que va por el canal de ALARMA— sonaba fuerte,
 * o sea exactamente al revés de para lo que existe. Y con la app cerrada no hay
 * WebView que lo toque. Así que ahí lo genera `Earcon.java`, pegado a la
 * locución y por el mismo canal de audio; `playEarcon` se hace a un lado para
 * no tocar dos tonos. Las notas de las dos tablas son las mismas.
 */

import { isNativeSpeechAvailable } from "./nativeSpeech.js";

/**
 * Silencio entre el fin del tono y la primera palabra, en ms.
 *
 * Pegados se oyen como una sola cosa y el tono deja de leerse como "atención,
 * ahí viene". Con 120 ms, probado en el teléfono, se oían demasiado seguidos.
 * Es el mismo valor que `EARCON_GAP_MS` de `AlertSpeaker.java`: la pausa no
 * puede cambiar según dónde se escuche la alerta.
 */
export const EARCON_SPEECH_GAP_MS = 500;

/** Silencio entre notas de un mismo tono, en s. Igual que `GAP_SEC` de `Earcon.java`. */
const NOTE_GAP_SEC = 0.04;

/** [frecuencia Hz, duración s] por nota; se tocan en secuencia. */
const EARCONS = {
  // Descendente urgente, tres notas: alarma clásica.
  security: [[880, 0.12], [740, 0.12], [880, 0.12]],
  // Agudo y repetido: "algo anda mal con el equipo".
  tamper: [[1200, 0.07], [1200, 0.07], [1200, 0.07]],
  // Dos notas ascendentes: neutro, informativo.
  geofence: [[600, 0.1], [800, 0.14]],
  // Grave sostenido: energía.
  power: [[300, 0.28]],
  // Media-alta corta: conducción.
  driving: [[520, 0.09], [660, 0.16]],
  // Una nota sola, discreta.
  device: [[480, 0.12]],
};

const ALERT_FAMILY = {
  SOS: "security", ACCIDENT: "security", FALL_DOWN: "security", THEFT_SUSPECTED: "security",
  TAMPERING: "tamper", REMOVING: "tamper", JAMMING: "tamper",
  GEOFENCE_ENTER: "geofence", GEOFENCE_EXIT: "geofence", GEOFENCE_DWELL: "geofence",
  BATTERY_CUT: "power", POWER_OFF: "power", POWER_RESTORED: "power", LOW_BATTERY: "power",
  OVERSPEED: "driving", VIBRATION: "driving", PARKED: "driving",
  ENGINE_ON: "device", ENGINE_OFF: "device", DEVICE_OFFLINE: "device",
  GPS_STALE: "device", DOOR: "device", COMMAND_RESULT: "device",
};

export function familyOf(alertType) {
  return ALERT_FAMILY[alertType] ?? "device";
}

/**
 * Cuánto pasa desde que arranca el tono hasta la primera palabra, en ms: las
 * notas, sus silencios y la pausa final.
 *
 * Lo necesita la espera de la locución nativa (`speakNative` en `speech.js`):
 * ahí el tono lo toca el motor del teléfono delante de la frase, y la espera
 * que no lo cuenta se termina antes que la frase.
 */
export function earconLeadMs(alertType) {
  const notes = EARCONS[familyOf(alertType)] ?? [];
  const tonoSec = notes.reduce((total, [, dur]) => total + dur + NOTE_GAP_SEC, 0);
  return Math.round(tonoSec * 1000) + EARCON_SPEECH_GAP_MS;
}

let ctx = null;

/**
 * AudioContext perezoso y compartido.
 *
 * Perezoso porque crear uno antes del primer gesto del usuario lo deja en
 * estado "suspended" y algunos navegadores lo cuentan igual contra el límite
 * de contextos por página. Compartido porque cada AudioContext reserva un
 * thread de audio del sistema y crear uno por alerta agota el límite (~6) en
 * una ráfaga.
 */
function audioContext() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

/**
 * Reanuda el contexto de audio. Debe llamarse DENTRO de un gesto del usuario
 * (click del toggle o del botón de prueba) o la política de autoplay deja el
 * contexto suspendido y ningún tono suena.
 *
 * @returns {Promise<boolean>} true si el audio quedó utilizable
 */
export async function unlockAudio() {
  const ac = audioContext();
  if (!ac) return false;
  try {
    if (ac.state === "suspended") await ac.resume();
    return ac.state === "running";
  } catch {
    return false;
  }
}

/**
 * Toca el earcon de una familia de alerta.
 *
 * Nunca lanza ni rechaza: un fallo de audio no puede tumbar el manejo de una
 * alerta. Si el contexto está bloqueado simplemente no suena nada.
 *
 * @param {string} alertType
 * @param {number} [volume] 0..1
 * @returns {Promise<void>} resuelve cuando terminó de sonar
 */
export function playEarcon(alertType, volume = 0.5) {
  // En la app lo toca el motor nativo, junto con la frase y por su mismo canal
  // de audio (ver el encabezado). Tocarlo también acá serían dos tonos.
  if (isNativeSpeechAvailable()) return Promise.resolve();

  const ac = audioContext();
  const notes = EARCONS[familyOf(alertType)];
  if (!ac || ac.state !== "running" || !notes) return Promise.resolve();

  try {
    let at = ac.currentTime;
    for (const [freq, dur] of notes) {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;

      // Rampas en vez de on/off seco: un gain que salta de 0 a 1 produce un
      // "click" audible (discontinuidad en la onda) más molesto que el tono.
      const peak = Math.max(0, Math.min(1, volume)) * 0.3;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);

      osc.connect(gain).connect(ac.destination);
      osc.start(at);
      osc.stop(at + dur + 0.02);
      at += dur + NOTE_GAP_SEC; // pequeño silencio entre notas
    }
    // Resuelve después de la pausa y no al terminar el tono: quien espera esta
    // promesa arranca la frase apenas resuelve. La cuenta sale de
    // `earconLeadMs` —la misma que usa la espera de la locución nativa— en vez
    // de rehacerse acá sobre la misma tabla: dos fórmulas para el mismo número
    // se separan en silencio y el tono y la frase dejan de encajar.
    return new Promise((resolve) => setTimeout(resolve, earconLeadMs(alertType)));
  } catch {
    return Promise.resolve();
  }
}

/** Solo para tests: descarta el contexto compartido. */
export function __resetAudioContext() {
  ctx = null;
}
